/**
 * groundingViolationDetector — INTEL-AEGIS-CA-4
 *
 * Post-generation grounding check for Sonnet drafts.
 * Runs a second Haiku pass to identify claims in the draft that are NOT
 * supported by the retrieved-data pack. Ungrounded spans trigger ≤2 retries.
 *
 * Root cause (CA-4): Sonnet drafts occasionally included facts not present in
 * the retrieved-data pack ("hallucinated facts"), causing grounded-output
 * contract violations caught late (or not at all).
 *
 * Patent substrate: reinforces F-AI-ATTR-GROUNDED-001 — the span-attribution
 * + retry mechanism is the core patentable method.
 *
 * Architecture:
 *   1. `detectGroundingViolations(draft, dataPackRefs, params)` — single check pass
 *   2. `withGroundingRetry(callFn, params, maxRetries)` — wraps a call in retry loop
 */

import governedAegisCall from './governedAegisCall.js';

export const MAX_RETRIES = 2;

/**
 * @typedef {object} GroundingViolation
 * @property {string} span         - the ungrounded text span
 * @property {string} reason       - why it's not grounded in the data pack
 * @property {number} startIndex   - approximate char offset in draft
 */

/**
 * @typedef {object} GroundingCheckResult
 * @property {boolean}             grounded        - true when zero violations found
 * @property {number}              groundingScore  - 0..1 (1 = fully grounded)
 * @property {GroundingViolation[]} violations
 * @property {string}              checkDecisionId - Haiku decision ID for audit trail
 * @property {boolean}             mocked          - true in stub/dev mode
 */

/**
 * Check a draft for grounding violations against the retrieved-data pack.
 *
 * The retrieved-data pack is passed as a stringified summary in `dataPackSummary`.
 * In production, the CF builds this from `dataPackRefs` (Firestore doc paths).
 * In dev/stub mode the check always passes (mocked=true).
 *
 * @param {object} opts
 * @param {string} opts.tenantId
 * @param {string} opts.agentId
 * @param {string} opts.module
 * @param {string} opts.draft              - the generated text to check
 * @param {string[]} [opts.dataPackRefs]   - Firestore paths of retrieved sources
 * @param {string} [opts.dataPackSummary]  - pre-serialized data pack (short-circuits CF lookup)
 * @returns {Promise<GroundingCheckResult>}
 */
export async function detectGroundingViolations({
  tenantId,
  agentId,
  module,
  draft,
  dataPackRefs = [],
  dataPackSummary = '',
}) {
  if (!draft || draft.trim().length === 0) {
    return { grounded: true, groundingScore: 1, violations: [], checkDecisionId: null, mocked: true };
  }

  const prompt = _buildGroundingPrompt(draft, dataPackSummary, dataPackRefs);

  const result = await governedAegisCall({
    tenantId,
    agentId,
    module,
    decisionType: 'grounding_check',
    // AEGIS routing: Haiku for classification/extraction
    modelId: 'claude-haiku-4-5-20251001',
    prompt,
    context: {
      materialityBand: 'ROUTINE',
      isDeclarative: false,
      isGroundingCheck: true,
    },
  });

  if (result.mocked) {
    return { grounded: true, groundingScore: 1, violations: [], checkDecisionId: result.decisionId, mocked: true };
  }

  const violations = _parseViolations(result.outputSummary, draft);
  const groundingScore = violations.length === 0 ? 1 : Math.max(0, 1 - violations.length * 0.15);

  return {
    grounded: violations.length === 0,
    groundingScore,
    violations,
    checkDecisionId: result.decisionId,
    mocked: false,
  };
}

/**
 * Wrap an AEGIS generation call with grounding-check retry logic.
 * On violation, retries up to `maxRetries` times with a prompt hint appended.
 *
 * @param {function} callFn         - async () => aegisResult (must return `.text` field)
 * @param {object}   groundingOpts  - forwarded to detectGroundingViolations
 * @param {number}   [maxRetries]   - default MAX_RETRIES (2)
 * @returns {Promise<{ result: object, groundingCheck: GroundingCheckResult, retryCount: number }>}
 */
export async function withGroundingRetry(callFn, groundingOpts, maxRetries = MAX_RETRIES) {
  let retryCount = 0;
  let lastResult = await callFn();
  let check = { grounded: true, groundingScore: 1, violations: [], mocked: true };

  if (!lastResult?.text) {
    // No text to check (stub / blocked result) — pass through
    return { result: lastResult, groundingCheck: check, retryCount: 0 };
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    check = await detectGroundingViolations({
      ...groundingOpts,
      draft: lastResult.text,
    });

    if (check.grounded) break;

    if (attempt < maxRetries) {
      // Retry: pass violation context back to call function
      lastResult = await callFn({
        _groundingRetryHint: _buildRetryHint(check.violations),
        _retryAttempt: attempt + 1,
      });
      retryCount++;
    }
  }

  return { result: lastResult, groundingCheck: check, retryCount };
}

function _buildGroundingPrompt(draft, dataPackSummary, dataPackRefs) {
  const refList = dataPackRefs.length > 0 ? `\n\nData sources consulted:\n${dataPackRefs.join('\n')}` : '';
  const dataPart = dataPackSummary
    ? `\n\nRetrieved data pack:\n${dataPackSummary}`
    : '';

  return [
    'You are a grounding auditor. Review the draft below and identify any factual claims NOT supported by the data pack.',
    'For each ungrounded span, output JSON: { "violations": [{ "span": "...", "reason": "..." }] }',
    'If all claims are grounded, output: { "violations": [] }',
    dataPart,
    refList,
    '\n\nDraft to check:\n',
    draft,
  ].filter(Boolean).join('');
}

function _parseViolations(outputSummary, draft) {
  if (!outputSummary) return [];
  const raw = outputSummary.violations || outputSummary.ungrounded_spans || [];
  if (!Array.isArray(raw)) return [];

  return raw
    .filter(v => v && typeof v.span === 'string' && v.span.trim())
    .map(v => ({
      span: v.span,
      reason: v.reason || 'not found in data pack',
      startIndex: typeof draft === 'string' ? draft.indexOf(v.span) : -1,
    }));
}

function _buildRetryHint(violations) {
  if (!violations?.length) return '';
  const spans = violations.map(v => `"${v.span}"`).join(', ');
  return `Previous response contained ungrounded claims: ${spans}. Revise to only include facts from the retrieved data.`;
}
